// Sport packs in the active language (#1118). The backend pack stays the
// English source of truth: in English the pack is shown as served. French
// and Spanish replace its fan-facing words key by key (stat key, position
// code, period code, …) from the shared catalogue, and anything the
// catalogue doesn't know (a stat a newer backend added, an unknown sport)
// keeps the pack's English. The words are the sport-packs namespace.

import { getLocale, messages } from '@/i18n'
import { sportPackMessages } from '@/i18n/catalogs/sport-packs'
import type { SportPack, SportTerms } from './SportContext'

interface Named {
  label: string
  short: string
}

/** One sport's words, loosely keyed: a pack may carry keys the catalogue doesn't. */
export interface SportWords {
  name: string
  terms: SportTerms
  venue: { singular: string; plural: string }
  stats?: Record<string, Named | undefined>
  teamStats?: Record<string, Named | undefined>
  derived?: Record<string, Named | undefined>
  actions?: Record<string, (Named & { feed: string }) | undefined>
  leaders?: Record<string, Named | undefined>
  positions?: Record<string, string | undefined>
  periods?: Record<string, string | undefined>
  standings?: Record<string, { label: string; description: string } | undefined>
}

/** The catalogue's words for a sport in the active language; null in English or for a sport it doesn't know. */
export function sportWords(sportId: string | null | undefined): SportWords | null {
  if (!sportId || getLocale() === 'en') return null
  const all: Record<string, SportWords | undefined> = messages(sportPackMessages)
  return all[sportId] ?? null
}

const relabel = <T extends { key: string; label: string; short: string }>(defs: T[] | undefined, words: Record<string, Named | undefined> | undefined): T[] =>
  (defs ?? []).map((d) => {
    const w = words?.[d.key]
    return w ? { ...d, label: w.label, short: w.short } : d
  })

// One localized copy per pack object and catalogue, so consumers get a stable
// reference (memo deps) until the language or the pack changes.
const cache = new WeakMap<SportPack, { words: SportWords; pack: SportPack }>()

/** The pack with its fan-facing words in the active language (the pack itself in English). */
export function localizePack(pack: SportPack): SportPack {
  const w = sportWords(pack.id)
  if (!w) return pack
  const hit = cache.get(pack)
  if (hit && hit.words === w) return hit.pack
  const out: SportPack = {
    ...pack,
    displayName: w.name,
    stats: relabel(pack.stats, w.stats),
    teamStats: relabel(pack.teamStats, w.teamStats),
    derivedStats: relabel(pack.derivedStats, w.derived),
    statActions: pack.statActions?.map((a) => {
      const x = w.actions?.[a.key]
      return x ? { ...a, label: x.label, short: x.short, feedLabel: x.feed } : a
    }),
    leaderboardCategories: (pack.leaderboardCategories ?? []).map((c) => {
      const x = w.leaders?.[c.key]
      return x ? { ...c, label: x.label, shortLabel: x.short } : c
    }),
    positions: (pack.positions ?? []).map((p) => ({ ...p, label: w.positions?.[p.code] ?? p.label })),
    periods: pack.periods && {
      ...pack.periods,
      prettyLabels: Object.fromEntries(Object.entries(pack.periods.prettyLabels ?? {}).map(([code, label]) => [code, w.periods?.[code] ?? label])),
    },
    standings: pack.standings && {
      ...pack.standings,
      columns: (pack.standings.columns ?? []).map((c) => {
        const x = w.standings?.[c.key]
        return x ? { ...c, label: x.label, description: x.description } : c
      }),
    },
    venueLabel: w.venue,
    terms: pack.terms && { ...pack.terms, ...w.terms },
  }
  cache.set(pack, { words: w, pack: out })
  return out
}

/** Every pack in a map, localized (the same map in English). */
export function localizePacks(packs: Record<string, SportPack>): Record<string, SportPack> {
  if (getLocale() === 'en') return packs
  return Object.fromEntries(Object.entries(packs).map(([id, p]) => [id, localizePack(p)]))
}
